import './UserList.css';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { HiOutlineMagnifyingGlass, HiOutlineArrowTopRightOnSquare, HiOutlinePlusCircle } from 'react-icons/hi2';
import { HiOutlineEllipsisVertical } from 'react-icons/hi2';
import { fetchUsers, UserData, changeUserRole, removeUser } from '../../services/userService';
import { useAuthStore } from '../../stores';
import { useRoles } from '../../hooks/useRoles';
import { roleColorClass, formatRoleLabel, isAdminTier } from '../../utils/roles';
import InviteUserDialog from '../../components/InviteUserDialog/InviteUserDialog';
import UserAssignmentsModal from '../../components/UserAssignmentsModal/UserAssignmentsModal';
import toast from 'react-hot-toast';

type StatusFilter = 'All' | 'Active' | 'Inactive';

function UserList() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const actionBtnRef = useRef<HTMLButtonElement>(null);
  const [assignmentsUser, setAssignmentsUser] = useState<UserData | null>(null);

  const user = useAuthStore((state) => state.user);
  const { roles } = useRoles();
  const currentUserRole = (user as any)?.role || '';

  const roleByName = useMemo(() => new Map(roles.map((r) => [r.name, r])), [roles]);

  // Get current user's role hierarchy from the fetched users list, falling back to the role
  // catalog's hierarchy for that role name.
  const currentUserHierarchy = useMemo(() => {
    const currentUser = users.find((u) => u.id === (user as any)?.id);
    return currentUser?.user_role?.role_hierarchy ?? roleByName.get(currentUserRole)?.role_hierarchy ?? null;
  }, [users, user, currentUserRole, roleByName]);

  // Mirrors app.user_assignment's insert/update/delete permissions, currently granted to the
  // top 2 hierarchy tiers only (administrator/executive) — see
  // server/hasura/metadata/.../app_user_assignment.yaml.
  const canManageAssignments = isAdminTier(currentUserHierarchy);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const data = await fetchUsers();
    setUsers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const searchMatch =
        searchQuery === '' ||
        (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.department || '').toLowerCase().includes(searchQuery.toLowerCase());
      const statusMatch =
        statusFilter === 'All' ||
        (statusFilter === 'Active' && u.is_active) ||
        (statusFilter === 'Inactive' && !u.is_active);
      return searchMatch && statusMatch;
    });
  }, [users, searchQuery, statusFilter]);

  // Build a map of user IDs to names for resolving invited_by
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    users.forEach((u) => {
      map[u.id] = u.name || u.email;
    });
    return map;
  }, [users]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inMenu = actionMenuRef.current?.contains(target);
      const inBtn = actionBtnRef.current?.contains(target);
      if (!inMenu && !inBtn) {
        setActionMenuUserId(null);
        setMenuPos(null);
      }
    };
    if (actionMenuUserId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuUserId]);

  const handleChangeRole = async (u: UserData, newRole: string) => {
    setActionMenuUserId(null);
    setMenuPos(null);
    const fromLabel = formatRoleLabel(roleByName.get(u.role), u.role);
    const toLabel = formatRoleLabel(roleByName.get(newRole), newRole);
    if (!window.confirm(`Change ${u.name || u.email} role from ${fromLabel} to ${toLabel}?`)) return;
    const result = await changeUserRole(u.id, newRole);
    if (result.success) {
      toast.success(`${u.name || u.email} role changed to ${toLabel}`);
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to change user role');
    }
  };

  const handleRemoveUser = async (u: UserData) => {
    setActionMenuUserId(null);
    setMenuPos(null);
    if (!window.confirm(`Remove ${u.name || u.email} from the organization? This action cannot be undone.`)) return;
    const result = await removeUser(u.id);
    if (result.success) {
      toast.success(`${u.name || u.email} removed from organization`);
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to remove user');
    }
  };

  const totalCount = users.length;
  const activeCount = users.filter((u) => u.is_active).length;

  // One count chip per role in the catalog, in hierarchy order — any role added to
  // app.user_role shows up here automatically, no code change needed.
  const roleCounts = useMemo(() => {
    const sorted = [...roles].sort((a, b) => (a.role_hierarchy ?? Number.MAX_SAFE_INTEGER) - (b.role_hierarchy ?? Number.MAX_SAFE_INTEGER));
    return sorted.map((r) => ({ role: r, count: users.filter((u) => u.role === r.name).length }));
  }, [roles, users]);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="user-list-page">
      <div className="user-list-header">
        <div className="user-list-title-row">
          <h1 className="user-list-title">User List</h1>
          <div className="user-list-summary">
            <span className="summary-chip">
              <span className="summary-chip-label">Total</span>
              <span className="summary-chip-value">{totalCount}</span>
            </span>
            {roleCounts.map(({ role, count }) => (
              <span key={role.name} className={`summary-chip ${roleColorClass(role.name, roles)}`}>
                <span className="summary-chip-label">{formatRoleLabel(role, role.name)}</span>
                <span className="summary-chip-value">{count}</span>
              </span>
            ))}
            <span className="summary-chip chip-active">
              <span className="summary-chip-label">Active</span>
              <span className="summary-chip-value">{activeCount}</span>
            </span>
          </div>
        </div>

        <div className="user-list-controls">
          <div className="user-search-bar">
            <HiOutlineMagnifyingGlass className="user-search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="user-search-input"
            />
          </div>
          <div className="user-filter-group">
            <div className="user-filter-pills">
              {(['All', 'Active', 'Inactive'] as StatusFilter[]).map((status) => (
                <button
                  key={status}
                  className={`filter-pill ${statusFilter === status ? 'active' : ''}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {status === 'All' ? 'All Status' : status}
                </button>
              ))}
            </div>
            <button className="invite-user-btn" onClick={() => setInviteDialogOpen(true)}>
              <HiOutlinePlusCircle />
              Invite User
            </button>
          </div>
        </div>
      </div>

      <div className="user-list-table-wrapper">
        <table className="user-list-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Role</th>
              <th>Department</th>
              <th>Title</th>
              <th>Invited By</th>
              <th>Status</th>
              <th>Joined At</th>
              <th>Activity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="user-empty-state">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((u, idx) => (
                <tr key={u.id}>
                  <td className="user-row-num">{idx + 1}</td>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">
                        {(u.name || u.email || '??').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <span className="user-name">{u.name || '-'}</span>
                        <span className="user-email">{u.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${roleColorClass(u.role, roles)}`}>
                      {formatRoleLabel(roleByName.get(u.role), u.role)}
                    </span>
                  </td>
                  <td>{u.department || '-'}</td>
                  <td>{u.title || '-'}</td>
                  <td>{u.invited_by ? (userNameMap[u.invited_by] || '-') : '-'}</td>
                  <td>
                    <span className={`status-badge ${u.is_active ? 'status-active' : 'status-pending'}`}>
                      {u.is_active ? 'Active' : 'Pending'}
                    </span>
                  </td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>
                    <a
                      href={`https://us.posthog.com/project/309021/person/${u.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="posthog-link"
                      title="View usage activity"
                    >
                      Usage Activity <HiOutlineArrowTopRightOnSquare />
                    </a>
                  </td>
                  <td className="user-actions-cell">
                    {u.id !== (user as any)?.id && (
                      <div className="user-actions-wrapper">
                        <button
                          className="user-actions-btn"
                          ref={actionMenuUserId === u.id ? actionBtnRef : undefined}
                          onClick={(e) => {
                            if (actionMenuUserId === u.id) {
                              setActionMenuUserId(null);
                              setMenuPos(null);
                            } else {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setMenuPos({ top: rect.bottom + 4, left: rect.right - 200 });
                              setActionMenuUserId(u.id);
                            }
                          }}
                          title="Actions"
                        >
                          <HiOutlineEllipsisVertical />
                        </button>
                        {actionMenuUserId === u.id && menuPos && (
                          <div className="user-actions-menu" ref={actionMenuRef} style={{ top: menuPos.top, left: menuPos.left }}>
                            {canManageAssignments && (
                              <>
                                <button
                                  className="user-action-item"
                                  onClick={() => { setActionMenuUserId(null); setAssignmentsUser(u); }}
                                >
                                  Manage Parent Access
                                </button>
                                <div className="user-action-label">Change Role</div>
                                {roles.filter((r) => r.name !== u.role).map((r) => (
                                  <button
                                    key={r.name}
                                    className="user-action-item"
                                    onClick={() => handleChangeRole(u, r.name)}
                                  >
                                    {formatRoleLabel(r, r.name)}
                                  </button>
                                ))}
                                <div className="user-action-divider" />
                              </>
                            )}
                            <button
                              className="user-action-item action-danger"
                              onClick={() => handleRemoveUser(u)}
                            >
                              Remove tool access
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="user-empty-state">
                  No users match the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <InviteUserDialog
        isOpen={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onUserInvited={loadUsers}
        currentUserRoleHierarchy={currentUserHierarchy ?? Infinity}
      />

      <UserAssignmentsModal
        isOpen={!!assignmentsUser}
        targetUser={assignmentsUser}
        onClose={() => setAssignmentsUser(null)}
      />
    </div>
  );
}

export default UserList;
